import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Building2, Shield, Wrench, FileText, MapPin, Download, ChevronDown, ChevronRight,
  CheckCircle2, Clock, AlertTriangle, Cpu, Loader2, ExternalLink, Layers, Search,
  ClipboardCheck, BarChart3, Tag, Box, Phone, Mail, Users, Calendar, Map, Package, FileDown
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HandoverPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [assetTrade, setAssetTrade] = useState('All');
  const [expandedAssets, setExpandedAssets] = useState({});
  const [showDemo, setShowDemo] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', company: '', building_name: '', phone: '', notes: '' });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [bundleLoading, setBundleLoading] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/handover/${token}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const filteredAssets = useMemo(() => {
    if (!data) return {};
    const result = {};
    for (const [trade, assets] of Object.entries(data.assets_by_trade || {})) {
      const filtered = assets.filter(a => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (
          (a.equipment_type || '').toLowerCase().includes(s) ||
          (a.make || '').toLowerCase().includes(s) ||
          (a.model || '').toLowerCase().includes(s) ||
          (a.location || '').toLowerCase().includes(s) ||
          (a.uniformat?.code || '').toLowerCase().includes(s)
        );
      });
      if (filtered.length > 0) result[trade] = filtered;
    }
    return result;
  }, [data, searchTerm]);

  const handleDownloadCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/handover/${token}/assets-csv`);
      const d = await res.json();
      if (!d.rows?.length) return;
      const headers = Object.keys(d.rows[0]);
      const csv = [headers.join(','), ...d.rows.map(r => headers.map(h => `"${(r[h] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${data?.project?.name || 'handover'}_assets.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const expiringWarranties = useMemo(() => {
    if (!data?.warranties) return [];
    const now = new Date();
    const soon = new Date();
    soon.setDate(soon.getDate() + 90);
    const out = [];
    for (const w of data.warranties) {
      for (const [kind, dateStr] of [['Parts', w.parts_expires], ['Labor', w.labor_expires]]) {
        if (!dateStr) continue;
        const d = new Date(dateStr);
        if (isNaN(d)) continue;
        if (d >= now && d <= soon) {
          const days = Math.ceil((d - now) / 86400000);
          out.push({ equipment: w.equipment, kind, date: dateStr, days });
        }
      }
    }
    return out.sort((a, b) => a.days - b.days);
  }, [data]);

  const handleDownloadBundle = async () => {
    setBundleLoading(true);
    try {
      const res = await fetch(`${API_URL}/handover/${token}/bundle.zip`);
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data?.project?.name || 'handover'}_Handover_Package.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
    setBundleLoading(false);
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.email) return;
    setDemoSubmitting(true);
    try {
      await fetch(`${API_URL}/handover/${token}/request-demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(demoForm),
      });
      setDemoSubmitted(true);
    } catch {}
    setDemoSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center">
      <div className="text-center">
        <Building2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-400 text-sm">Handover package not found.</p>
      </div>
    </div>
  );

  const p = data.project || {};
  const stats = data.stats || {};

  // Pin → asset card: open the Asset Cards tab, expand the asset, scroll to it.
  const jumpToAsset = (assetId) => {
    if (!assetId) return;
    setSearchTerm('');
    setActiveSection('assets');
    setExpandedAssets(prev => ({ ...prev, [assetId]: true }));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="handover-asset-${assetId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  };

  const NAV = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'assets', label: 'Asset Cards', icon: Cpu },
    { id: 'floorplan', label: 'Equipment Locations', icon: Map },
    { id: 'warranties', label: 'Warranty Binder', icon: Shield },
    { id: 'pm', label: 'PM Tasks', icon: Wrench },
    { id: 'om', label: 'O&M Manuals', icon: FileText },
    { id: 'contacts', label: 'Contacts', icon: Phone },
    { id: 'submittals', label: 'Submittals', icon: ClipboardCheck },
    { id: 'audit', label: 'Audit Summary', icon: FileText },
    { id: 'downloads', label: 'Downloads', icon: Download },
  ];

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white" data-testid="handover-portal">
      {/* Header */}
      <header className="border-b border-slate-800/60 bg-[#0c1020]/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center">
              <Building2 className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-white tracking-tight">{p.name || 'Project Handover'}</h1>
              <p className="text-[10px] text-slate-500 font-mono">{p.number} {p.address ? `— ${p.address}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-500 font-mono">Prepared by {p.pm_name || data.generated_by}</span>
            <div className="w-px h-4 bg-slate-800" />
            <span className="text-[9px] px-2 py-1 rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-heading uppercase tracking-wider">
              Closeout Package
            </span>
          </div>
        </div>

        {/* Nav */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto pb-0">
          {NAV.map(n => (
            <button key={n.id} onClick={() => setActiveSection(n.id)}
              data-testid={`nav-${n.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-heading uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
                activeSection === n.id
                  ? 'text-blue-400 border-blue-500'
                  : 'text-slate-500 border-transparent hover:text-slate-300 hover:border-slate-700'
              }`}>
              <n.icon className="w-3 h-3" /> {n.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview */}
        {activeSection === 'overview' && (
          <div className="space-y-8" data-testid="section-overview">
            {/* Welcome banner */}
            <div className="relative overflow-hidden rounded-sm border border-blue-800/30 bg-gradient-to-br from-blue-950/50 via-indigo-950/40 to-slate-900/30 px-6 py-5" data-testid="handover-welcome">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 text-blue-300" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base md:text-lg font-semibold text-white">
                    Welcome to your digital turnover for {p.name || 'your building'}
                  </h2>
                  <p className="text-xs text-slate-400 leading-relaxed mt-1 max-w-2xl">
                    Everything your GC is handing over lives here — equipment, warranties, maintenance tasks and as-builts.
                    Start with <span className="text-slate-200">Turnover Completeness</span> below to confirm nothing's missing,
                    open any item under <span className="text-slate-200">Asset Cards</span>, see where each unit sits in{' '}
                    <span className="text-slate-200">Equipment Locations</span>, or grab the files from <span className="text-slate-200">Downloads</span>.
                  </p>
                </div>
              </div>
            </div>
            {/* At-a-glance turnover summary */}
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm px-5 py-4" data-testid="handover-summary-line">
              <p className="text-sm text-slate-300 leading-relaxed">
                This turnover package covers{' '}
                <span className="font-semibold text-white font-mono">{stats.total_fixtures ?? stats.total_assets}</span> installed fixtures across{' '}
                <span className="font-semibold text-white font-mono">{stats.total_assets}</span> equipment types ·{' '}
                <span className="font-semibold text-emerald-400 font-mono">{stats.with_warranty}</span> warranties the GC owes you ·{' '}
                <span className="font-semibold text-amber-400 font-mono">{stats.total_om_tasks}</span> maintenance tasks to keep them running.
              </p>
            </div>
            {/* Warranty Expiration Alert */}
            {expiringWarranties.length > 0 && (
              <div className="bg-amber-950/30 border border-amber-700/40 rounded-sm p-4" data-testid="warranty-alert-banner">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-300">
                      {expiringWarranties.length} warrant{expiringWarranties.length === 1 ? 'y' : 'ies'} expiring within 90 days
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {expiringWarranties.slice(0, 6).map((w, i) => (
                        <span key={i} className="text-[10px] font-mono px-2 py-1 rounded-sm bg-amber-500/10 border border-amber-600/30 text-amber-200" data-testid={`warranty-alert-item-${i}`}>
                          {w.equipment} — {w.kind} exp. {w.date} ({w.days}d)
                        </span>
                      ))}
                      {expiringWarranties.length > 6 && (
                        <button onClick={() => setActiveSection('warranties')} className="text-[10px] text-amber-400 hover:text-amber-300 underline">
                          +{expiringWarranties.length - 6} more in Warranty Binder
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Assets', value: stats.total_assets, icon: Cpu, color: 'text-blue-400' },
                { label: 'Total Fixtures', value: stats.total_fixtures ?? stats.total_assets, icon: Box, color: 'text-indigo-400' },
                { label: 'Trades / Divisions', value: stats.trade_count, icon: Layers, color: 'text-violet-400' },
                { label: 'Warranties Found', value: stats.with_warranty, icon: Shield, color: 'text-emerald-400' },
                { label: 'O&M Tasks', value: stats.total_om_tasks, icon: Wrench, color: 'text-amber-400' },
                { label: 'UniFormat Classified', value: stats.with_uniformat, icon: Tag, color: 'text-teal-400' },
                { label: 'Approved Submittals', value: stats.approved_submittals, icon: ClipboardCheck, color: 'text-cyan-400' },
                { label: 'Warranty Items', value: stats.total_warranties, icon: Clock, color: 'text-rose-400' },
                { label: 'Trades', value: (stats.trades || []).join(', '), icon: Box, color: 'text-slate-400', small: true },
              ].map((s, i) => (
                <div key={i} className="bg-slate-900/50 border border-slate-800/60 rounded-sm px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                    <span className="text-[10px] font-heading uppercase tracking-widest text-slate-500">{s.label}</span>
                  </div>
                  <p className={`${s.small ? 'text-xs' : 'text-xl'} font-semibold text-white font-mono`}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Per-trade Turnover Completeness */}
            {Object.keys(stats.trade_rollups || {}).length > 0 && (
              <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6" data-testid="handover-completeness">
                <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400 mb-1">Turnover Completeness</h2>
                <p className="text-[11px] text-slate-500 mb-5">Warranty &amp; maintenance coverage per trade — confirm nothing is missing before you accept the building.</p>
                <div className="space-y-5">
                  {Object.entries(stats.trade_rollups).map(([trade, r]) => {
                    const types = r.types || 0;
                    const wPct = types ? Math.round((r.with_warranty / types) * 100) : 0;
                    const oPct = types ? Math.round((r.with_om / types) * 100) : 0;
                    const bar = (label, val, total, pct, color) => (
                      <div className="flex-1 min-w-0" data-testid={`completeness-${trade}-${label.toLowerCase()}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
                          <span className="text-[11px] font-mono text-slate-300">{val}/{total} <span className="text-slate-600">· {pct}%</span></span>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                    return (
                      <div key={trade} data-testid={`completeness-trade-${trade}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Layers className="w-3.5 h-3.5 text-violet-400" />
                          <span className="text-xs font-heading uppercase tracking-widest text-white">{trade}</span>
                          <span className="text-[10px] font-mono text-slate-500">{r.fixtures} fixtures · {types} types</span>
                        </div>
                        <div className="flex items-center gap-6">
                          {bar('Warranty', r.with_warranty || 0, types, wPct, 'bg-emerald-500')}
                          {bar('O&M', r.with_om || 0, types, oPct, 'bg-amber-500')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6">
              <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400 mb-4">Project Information</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {[
                  ['Project Name', p.name],
                  ['Project Number', p.number],
                  ['Address', p.address],
                  ['General Contractor', p.gc_name],
                  ['Project Manager', p.pm_name || data.generated_by],
                  ['Completion Date', p.completion_date],
                ].map(([label, val], i) => (
                  <div key={i}>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{label}</p>
                    <p className="text-white font-mono text-xs">{val || '—'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* FMIQ-One roll-in teaser */}
            <div className="bg-gradient-to-r from-blue-950/40 to-indigo-950/40 border border-blue-800/30 rounded-sm p-6" data-testid="operate-cta">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-white mb-2">Want this data living in <span className="text-blue-300">FMIQ-One</span>?</h3>
                  <p className="text-xs text-slate-400 leading-relaxed max-w-lg">
                    This package is already structured for direct import — every asset, warranty, PM schedule and document carries
                    linking keys. Roll it straight into <strong className="text-blue-300">FMIQ-One</strong>, our maintenance system, and
                    get live warranty-expiration alerts, preventive-maintenance scheduling, and equipment lifecycle tracking —
                    with zero re-entry. Prefer your own CMMS? The workbook and CSVs import there too.
                  </p>
                </div>
                <button onClick={() => setShowDemo(true)} data-testid="request-demo-btn"
                  className="shrink-0 ml-6 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
                  Roll Into FMIQ-One
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Asset Cards */}
        {activeSection === 'assets' && (
          <div className="space-y-6" data-testid="section-assets">
            <div className="flex items-center gap-3">
              <Search className="w-4 h-4 text-slate-500" />
              <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search assets by type, make, model, location, or UniFormat code..."
                data-testid="asset-search"
                className="flex-1 px-3 py-2 text-xs bg-slate-900 border border-slate-800 rounded-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50" />
            </div>

            {(() => {
              const tradeNames = Object.keys(data.assets_by_trade || {});
              if (tradeNames.length <= 1) return null;
              const tabs = ['All', ...tradeNames];
              return (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="asset-trade-filter">
                  <Layers className="w-3.5 h-3.5 text-violet-400 mr-1" />
                  {tabs.map(t => {
                    const count = t === 'All'
                      ? (stats.total_assets ?? 0)
                      : (data.assets_by_trade[t] || []).length;
                    return (
                      <button key={t} onClick={() => setAssetTrade(t)}
                        data-testid={`asset-trade-tab-${t}`}
                        className={`px-3 py-1.5 rounded-sm text-[11px] font-medium border transition-colors ${
                          t === assetTrade
                            ? 'bg-violet-600/20 border-violet-500/60 text-violet-200'
                            : 'bg-slate-900/50 border-slate-800/60 text-slate-400 hover:text-slate-200 hover:border-slate-700'
                        }`}>
                        {t}
                        <span className="ml-1.5 text-[9px] opacity-70">{count}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}

            {Object.entries(filteredAssets)
              .filter(([trade]) => assetTrade === 'All' || trade === assetTrade)
              .map(([trade, assets]) => (
              <div key={trade} className="border border-slate-800/60 rounded-sm overflow-hidden">
                <div className="bg-slate-900/80 px-4 py-2.5 flex items-center justify-between border-b border-slate-800/40">
                  <div className="flex items-center gap-2">
                    <Layers className="w-3.5 h-3.5 text-violet-400" />
                    <span className="text-xs font-heading uppercase tracking-widest text-white">{trade}</span>
                  </div>
                  <span className="text-[10px] font-mono text-slate-500" data-testid={`handover-trade-rollup-${trade}`}>
                    {assets.reduce((n, x) => n + (x.quantity ?? 1), 0)} fixtures · {assets.length} type{assets.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="divide-y divide-slate-800/30">
                  {assets.map(a => (
                    <AssetCardPublic key={a.id} asset={a}
                      warrantyStart={data.warranty_start}
                      expanded={expandedAssets[a.id]}
                      onToggle={() => setExpandedAssets(prev => ({ ...prev, [a.id]: !prev[a.id] }))} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Floor Plan */}
        {activeSection === 'floorplan' && (
          <div className="space-y-4" data-testid="section-floorplan">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400">
                Equipment Locations
              </h2>
              {data.drawings?.length > 0 && (
                <span className="text-[10px] font-mono text-slate-500">
                  {data.drawings.length} drawing{data.drawings.length > 1 ? 's' : ''} · {data.drawings.reduce((n, d) => n + (d.pins?.length || 0), 0)} pins
                </span>
              )}
            </div>
            {data.drawings?.length > 0 ? (
              <DrawingsViewer drawings={data.drawings} onPinClick={jumpToAsset} />
            ) : data.floor_plan ? (
              <FloorPlanViewer floorPlan={data.floor_plan} onMarkerClick={jumpToAsset} />
            ) : (
              <p className="text-xs text-slate-500 py-8 text-center">No drawings with located equipment have been published for this project.</p>
            )}
          </div>
        )}

        {/* Warranty Binder */}
        {activeSection === 'warranties' && (
          <div className="space-y-4" data-testid="section-warranties">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400">Warranty Binder — {data.warranties?.length || 0} Items</h2>
              {data.warranty_start && (
                <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" />
                  Warranty Start: <span className="text-emerald-400">{data.warranty_start}</span>
                </span>
              )}
            </div>
            {(data.warranties || []).length === 0 ? (
              <p className="text-xs text-slate-500 py-8 text-center">No warranty data available. Run the AI Doc Finder to populate warranty information.</p>
            ) : (
              <div className="border border-slate-800/60 rounded-sm overflow-hidden overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="bg-slate-900/80 border-b border-slate-800/40">
                      {['Equipment', 'Trade', 'Component', 'Coverage', 'Parts', 'Labor', 'Parts Exp.', 'Labor Exp.', 'Registration'].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-heading uppercase tracking-wider text-slate-500 text-[10px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.warranties.map((w, i) => {
                      const partsExpired = w.parts_expires && new Date(w.parts_expires) < new Date();
                      const laborExpired = w.labor_expires && new Date(w.labor_expires) < new Date();
                      return (
                        <tr key={i} className="border-b border-slate-800/20 hover:bg-slate-900/30">
                          <td className="px-3 py-2 text-white font-mono">{w.equipment}</td>
                          <td className="px-3 py-2 text-slate-400">{w.trade}</td>
                          <td className="px-3 py-2 text-slate-300">{w.component}</td>
                          <td className="px-3 py-2 text-slate-300">{w.coverage}</td>
                          <td className="px-3 py-2 text-slate-300 font-mono">{w.parts_years ? `${w.parts_years}yr` : '—'}</td>
                          <td className="px-3 py-2 text-slate-300 font-mono">{w.labor_years ? `${w.labor_years}yr` : '—'}</td>
                          <td className="px-3 py-2">
                            {w.parts_expires ? (
                              <span className={`font-mono ${partsExpired ? 'text-rose-400' : 'text-emerald-400'}`}>{w.parts_expires}</span>
                            ) : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {w.labor_expires ? (
                              <span className={`font-mono ${laborExpired ? 'text-rose-400' : 'text-emerald-400'}`}>{w.labor_expires}</span>
                            ) : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {w.registration_url ? (
                              <a href={w.registration_url} target="_blank" rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" /> Register
                              </a>
                            ) : w.requires_registration ? (
                              <span className="text-amber-400 text-[10px]">Required</span>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* PM Tasks — Preventive Maintenance schedule */}
        {activeSection === 'pm' && (
          <div className="space-y-8" data-testid="section-pm">
            {/* Preventive Maintenance Tasks */}
            <div className="space-y-3" data-testid="om-tasks-block">
              <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <Wrench className="w-3.5 h-3.5 text-amber-400" />
                Preventive Maintenance Tasks — {data.om_tasks?.length || 0}
              </h2>
              {(data.om_tasks || []).length === 0 ? (
                <p className="text-xs text-slate-500 py-6 text-center">No maintenance tasks available. Run the AI Doc Finder to populate PM tasks.</p>
              ) : (
                <div className="border border-slate-800/60 rounded-sm overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-slate-900/80 border-b border-slate-800/40">
                        {['Equipment', 'Trade', 'Task', 'Frequency', 'Category'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-heading uppercase tracking-wider text-slate-500 text-[10px]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.om_tasks.map((om, i) => (
                        <tr key={i} className="border-b border-slate-800/20 hover:bg-slate-900/30" data-testid={`om-task-row-${i}`}>
                          <td className="px-3 py-2 text-white font-mono">{om.equipment}</td>
                          <td className="px-3 py-2 text-slate-400">{om.trade}</td>
                          <td className="px-3 py-2 text-slate-300">{om.task}</td>
                          <td className="px-3 py-2 text-amber-400 font-mono">{om.frequency}</td>
                          <td className="px-3 py-2 text-slate-300">{om.category || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* O&M Manuals — OEM documents & reference manuals */}
        {activeSection === 'om' && (
          <div className="space-y-8" data-testid="section-om">
            {/* O&M Documents & Manuals */}
            <div className="space-y-3" data-testid="om-docs-block">
              <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-blue-400" />
                O&M Documents & Manuals — {data.om_documents?.length || 0}
              </h2>
              {(data.om_documents || []).length === 0 ? (
                <p className="text-xs text-slate-500 py-6 text-center">No OEM manuals or reference documents linked yet.</p>
              ) : (
                <div className="border border-slate-800/60 rounded-sm overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-slate-900/80 border-b border-slate-800/40">
                        {['Equipment', 'Trade', 'Document', 'Source'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-heading uppercase tracking-wider text-slate-500 text-[10px]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.om_documents.map((d, i) => (
                        <tr key={i} className="border-b border-slate-800/20 hover:bg-slate-900/30 bg-blue-950/10" data-testid={`om-doc-row-${i}`}>
                          <td className="px-3 py-2 text-white font-mono">{d.equipment}</td>
                          <td className="px-3 py-2 text-slate-400">{d.trade}</td>
                          <td className="px-3 py-2">
                            {d.url ? (
                              <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid={`om-doc-link-${i}`}>
                                <ExternalLink className="w-3 h-3" /> {d.title}
                              </a>
                            ) : d.title}
                          </td>
                          <td className="px-3 py-2 text-slate-500">{d.source || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Emergency & Service Contacts */}
        {activeSection === 'contacts' && (
          <div className="space-y-6" data-testid="section-contacts">
            <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400">Emergency & Service Contacts</h2>
            {(data.contacts || []).length === 0 ? (
              <p className="text-xs text-slate-500 py-8 text-center">No contacts have been added to this project yet.</p>
            ) : (
              <>
                {/* Group by category */}
                {['emergency', 'service', 'general'].map(cat => {
                  const catContacts = (data.contacts || []).filter(c => (c.category || 'general') === cat);
                  if (catContacts.length === 0) return null;
                  const catLabel = cat === 'emergency' ? 'Emergency Contacts' : cat === 'service' ? 'Service Providers' : 'General Contacts';
                  const catColor = cat === 'emergency' ? 'rose' : cat === 'service' ? 'amber' : 'slate';
                  return (
                    <div key={cat}>
                      <h3 className={`text-xs font-heading uppercase tracking-widest text-${catColor}-400 mb-3 flex items-center gap-2`}>
                        {cat === 'emergency' && <AlertTriangle className="w-3.5 h-3.5" />}
                        {cat === 'service' && <Wrench className="w-3.5 h-3.5" />}
                        {cat === 'general' && <Users className="w-3.5 h-3.5" />}
                        {catLabel}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {catContacts.map(c => (
                          <div key={c.id} className={`bg-slate-900/50 border rounded-sm p-4 ${
                            cat === 'emergency' ? 'border-rose-800/30' : cat === 'service' ? 'border-amber-800/30' : 'border-slate-800/60'
                          }`} data-testid={`contact-card-${c.id}`}>
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="text-sm font-medium text-white">{c.name}</p>
                                {c.role && <p className="text-[10px] text-slate-400">{c.role}</p>}
                              </div>
                              {c.company && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-slate-800 text-slate-400 border border-slate-700">{c.company}</span>
                              )}
                            </div>
                            <div className="space-y-1.5">
                              {c.phone && (
                                <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300">
                                  <Phone className="w-3 h-3" /> {c.phone}
                                </a>
                              )}
                              {c.email && (
                                <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300">
                                  <Mail className="w-3 h-3" /> {c.email}
                                </a>
                              )}
                              {c.notes && (
                                <p className="text-[10px] text-slate-500 mt-1">{c.notes}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* Submittals */}
        {activeSection === 'submittals' && (
          <div className="space-y-4" data-testid="section-submittals">
            <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400">Approved Submittals — {data.approved_submittals?.length || 0}</h2>
            {(data.approved_submittals || []).length === 0 ? (
              <p className="text-xs text-slate-500 py-8 text-center">No approved submittals found.</p>
            ) : (
              <div className="border border-slate-800/60 rounded-sm overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="bg-slate-900/80 border-b border-slate-800/40">
                      {['Spec Section', 'Description', 'Division', 'Status', 'Approved Items'].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-heading uppercase tracking-wider text-slate-500 text-[10px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.approved_submittals.map((s, i) => (
                      <tr key={i} className="border-b border-slate-800/20 hover:bg-slate-900/30">
                        <td className="px-3 py-2 text-white font-mono">{s.spec_section}</td>
                        <td className="px-3 py-2 text-slate-300">{s.description}</td>
                        <td className="px-3 py-2 text-slate-400">{s.division}</td>
                        <td className="px-3 py-2">
                          <span className="px-1.5 py-0.5 rounded-sm text-[9px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            {s.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-white font-mono">{s.approved_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Audit Summary */}
        {activeSection === 'audit' && (
          <div className="space-y-4" data-testid="section-audit">
            <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400">Handover Readiness Audit</h2>
            {!data.audit ? (
              <p className="text-xs text-slate-500 py-8 text-center">No audit data available. Run a Handover Readiness Audit from the project.</p>
            ) : (
              <div className="space-y-4">
                {/* Overall grade */}
                <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6 flex items-center gap-6">
                  <div className={`text-4xl font-bold font-mono ${
                    data.audit.overall_grade === 'A' ? 'text-emerald-400' :
                    data.audit.overall_grade === 'B' ? 'text-blue-400' :
                    data.audit.overall_grade === 'C' ? 'text-amber-400' : 'text-rose-400'
                  }`}>{data.audit.overall_grade || '?'}</div>
                  <div>
                    <p className="text-sm text-white">Overall Handover Readiness</p>
                    <p className="text-xs text-slate-400 mt-0.5">{data.audit.overall_score || 0}% — {data.audit.summary || ''}</p>
                  </div>
                </div>

                {/* Per-trade grades */}
                {(data.audit.trade_grades || []).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {data.audit.trade_grades.map((tg, i) => (
                      <div key={i} className="bg-slate-900/50 border border-slate-800/60 rounded-sm px-4 py-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-heading uppercase tracking-wider text-slate-400">{tg.trade}</span>
                          <span className={`text-sm font-bold font-mono ${
                            tg.grade === 'A' ? 'text-emerald-400' : tg.grade === 'B' ? 'text-blue-400' :
                            tg.grade === 'C' ? 'text-amber-400' : 'text-rose-400'
                          }`}>{tg.grade}</span>
                        </div>
                        <p className="text-[10px] text-slate-500">{tg.score}% — {tg.note || ''}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Downloads */}
        {activeSection === 'downloads' && (
          <div className="space-y-6" data-testid="section-downloads">
            <h2 className="text-sm font-heading uppercase tracking-widest text-slate-400">Download Handover Package</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button onClick={handleDownloadCSV} data-testid="download-csv-btn"
                className="flex items-center gap-4 bg-slate-900/50 border border-slate-800/60 rounded-sm p-5 hover:border-blue-500/30 transition-colors text-left group">
                <Download className="w-6 h-6 text-blue-400 group-hover:text-blue-300 transition-colors" />
                <div>
                  <p className="text-sm font-medium text-white">Asset Schedule (CSV)</p>
                  <p className="text-[10px] text-slate-500">Full equipment list with specs, UniFormat codes, warranty status. Import into any CMMS/CAFM.</p>
                </div>
              </button>
              <button onClick={handleDownloadBundle} disabled={bundleLoading} data-testid="download-bundle-btn"
                className="flex items-center gap-4 bg-slate-900/50 border border-slate-800/60 rounded-sm p-5 hover:border-blue-500/30 transition-colors text-left group disabled:opacity-60">
                {bundleLoading ? <Loader2 className="w-6 h-6 text-blue-400 animate-spin" /> : <Package className="w-6 h-6 text-blue-400 group-hover:text-blue-300 transition-colors" />}
                <div>
                  <p className="text-sm font-medium text-white">Full PDF Bundle (ZIP)</p>
                  <p className="text-[10px] text-slate-500">{bundleLoading ? 'Generating package…' : 'Cover page, asset cards, warranty binder, O&M index + asset CSV in one ZIP.'}</p>
                </div>
              </button>
              <a href={`${API_URL}/handover/${token}/tables.xlsx`} target="_blank" rel="noopener noreferrer"
                data-testid="download-xlsx-btn"
                className="flex items-center gap-4 bg-slate-900/50 border border-slate-800/60 rounded-sm p-5 hover:border-emerald-500/30 transition-colors text-left group">
                <Layers className="w-6 h-6 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
                <div>
                  <p className="text-sm font-medium text-white">Full Data Workbook (Excel)</p>
                  <p className="text-[10px] text-slate-500">Every table on its own tab — Assets, Warranties, PM Tasks, O&M Docs, Contacts, Submittals, Drawings — linked by project & asset IDs.</p>
                </div>
              </a>
            </div>

            {/* Document Links — Drawings */}
            {(data.drawing_sets || []).length > 0 && (
              <div className="space-y-3" data-testid="downloads-drawings">
                <h3 className="text-xs font-heading uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5 text-violet-400" /> As-Built Drawing Sets
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.drawing_sets.map(ds => (
                    <a key={ds.id} href={ds.download_url} target="_blank" rel="noopener noreferrer"
                      data-testid={`drawing-download-${ds.id}`}
                      className="flex items-center gap-3 bg-slate-900/50 border border-slate-800/60 rounded-sm p-4 hover:border-blue-500/30 transition-colors group">
                      <FileDown className="w-5 h-5 text-violet-400 shrink-0 group-hover:text-violet-300" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-white truncate">{ds.name || ds.file_name}</p>
                        <p className="text-[10px] text-slate-500">{ds.revision_label} {ds.total_sheets ? `• ${ds.total_sheets} sheets` : ''}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Document Links — Submittals */}
            {(data.submittal_files || []).length > 0 && (
              <div className="space-y-3" data-testid="downloads-submittals">
                <h3 className="text-xs font-heading uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <ClipboardCheck className="w-3.5 h-3.5 text-cyan-400" /> Submittal Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.submittal_files.map(sf => (
                    <a key={sf.id} href={sf.download_url} target="_blank" rel="noopener noreferrer"
                      data-testid={`submittal-download-${sf.id}`}
                      className="flex items-center gap-3 bg-slate-900/50 border border-slate-800/60 rounded-sm p-4 hover:border-blue-500/30 transition-colors group">
                      <FileDown className="w-5 h-5 text-cyan-400 shrink-0 group-hover:text-cyan-300" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-white truncate">{sf.file_name}</p>
                        <p className="text-[10px] text-slate-500">{sf.spec_section || sf.trade_name || 'Submittal'}{sf.status ? ` • ${sf.status}` : ''}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <p className="text-[10px] text-slate-600 text-center mt-4">
              All formats designed for direct import into CMMS, CAFM, and facilities management systems.
            </p>

            {/* FMiQ-Operate CTA (repeated) */}
            <div className="bg-gradient-to-r from-blue-950/40 to-indigo-950/40 border border-blue-800/30 rounded-sm p-6 text-center">
              <h3 className="text-sm font-semibold text-white mb-2">Skip the Import — Go Straight to FMiQ-Operate</h3>
              <p className="text-xs text-slate-400 mb-4 max-w-md mx-auto">
                Why download and re-import? FMiQ-Operate takes this exact handover package and turns it into a live operational system — warranty tracking, PM scheduling, work orders, all pre-loaded.
              </p>
              <button onClick={() => setShowDemo(true)} data-testid="download-demo-btn"
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
                Request Demo
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/40 mt-12 py-6">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <p className="text-[10px] text-slate-600 font-mono">Generated by FMiQ-One Closeout Intelligence</p>
          <p className="text-[10px] text-slate-600">{data.generated_at ? new Date(data.generated_at).toLocaleDateString() : ''}</p>
        </div>
      </footer>

      {/* Demo Request Modal */}
      {showDemo && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDemo(false)} data-testid="demo-modal">
          <div className="bg-[#0f1525] border border-slate-800 rounded-sm shadow-2xl w-full max-w-md"
            onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-800/60">
              <h3 className="text-sm font-semibold text-white">Roll this data into FMIQ-One</h3>
              <p className="text-[10px] text-slate-500 mt-1">See your handover package go live in FMIQ-One — our maintenance system — with warranty alerts, PM scheduling and lifecycle tracking.</p>
            </div>
            {demoSubmitted ? (
              <div className="p-6 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                <p className="text-sm text-white font-medium">Demo Request Submitted</p>
                <p className="text-xs text-slate-400 mt-1">Our team will reach out shortly to set up your demo.</p>
                <button onClick={() => setShowDemo(false)} className="mt-4 px-4 py-2 text-xs text-slate-400 hover:text-white transition-colors">Close</button>
              </div>
            ) : (
              <form onSubmit={handleDemoSubmit} className="p-6 space-y-3">
                {[
                  { key: 'name', label: 'Full Name', required: true },
                  { key: 'email', label: 'Email', type: 'email', required: true },
                  { key: 'company', label: 'Company / Organization' },
                  { key: 'building_name', label: 'Building Name' },
                  { key: 'phone', label: 'Phone' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">
                      {f.label} {f.required && <span className="text-rose-400">*</span>}
                    </label>
                    <input value={demoForm[f.key]} onChange={e => setDemoForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      type={f.type || 'text'} required={f.required}
                      data-testid={`demo-${f.key}`}
                      className="w-full px-3 py-2 text-xs bg-slate-900 border border-slate-700 rounded-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50" />
                  </div>
                ))}
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Notes</label>
                  <textarea value={demoForm.notes} onChange={e => setDemoForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2} data-testid="demo-notes"
                    className="w-full px-3 py-2 text-xs bg-slate-900 border border-slate-700 rounded-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50 resize-none" />
                </div>
                <button type="submit" disabled={demoSubmitting || !demoForm.name || !demoForm.email}
                  data-testid="demo-submit-btn"
                  className="w-full mt-2 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors disabled:opacity-40">
                  {demoSubmitting ? 'Submitting...' : 'Request FMIQ-One Access'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function DrawingsViewer({ drawings, onPinClick }) {
  // Group drawings by trade/discipline so multi-trade projects get a filter.
  const trades = useMemo(() => {
    const map = {};
    for (const d of drawings) {
      const t = (d.trade || d.discipline || 'Other').trim() || 'Other';
      (map[t] = map[t] || []).push(d);
    }
    return map;
  }, [drawings]);
  const tradeNames = Object.keys(trades);
  const multiTrade = tradeNames.length > 1;

  const [trade, setTrade] = useState('All');
  const [active, setActive] = useState(0);

  const sheets = trade === 'All' ? drawings : (trades[trade] || drawings);
  const d = sheets[active] || sheets[0];

  const selectTrade = (t) => { setTrade(t); setActive(0); };

  const floorPlan = {
    image_url: d.image_url,
    width: d.width,
    height: d.height,
    sheet_name: d.sheet_name,
    markers: d.pins || [],
  };
  return (
    <div className="space-y-3" data-testid="drawings-viewer">
      {/* Trade filter (multi-trade projects only) */}
      {multiTrade && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="drawing-trade-filter">
          <Layers className="w-3.5 h-3.5 text-violet-400 mr-1" />
          {['All', ...tradeNames].map(t => (
            <button
              key={t}
              onClick={() => selectTrade(t)}
              data-testid={`drawing-trade-${t}`}
              className={`px-3 py-1.5 rounded-sm text-[11px] font-medium border transition-colors ${
                t === trade
                  ? 'bg-violet-600/20 border-violet-500/60 text-violet-200'
                  : 'bg-slate-900/50 border-slate-800/60 text-slate-400 hover:text-slate-200 hover:border-slate-700'
              }`}>
              {t}
              <span className="ml-1.5 text-[9px] opacity-70">{t === 'All' ? drawings.length : trades[t].length}</span>
            </button>
          ))}
        </div>
      )}
      {/* Sheet selector */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {sheets.map((dr, i) => (
          <button
            key={dr.sheet_id}
            onClick={() => setActive(i)}
            data-testid={`drawing-tab-${dr.sheet_number}`}
            className={`px-2.5 py-1 rounded-sm text-[11px] font-mono border transition-colors ${
              i === active
                ? 'bg-blue-600/20 border-blue-500/60 text-blue-200'
                : 'bg-slate-900/50 border-slate-800/60 text-slate-400 hover:text-slate-200 hover:border-slate-700'
            }`}>
            {dr.sheet_number}
            <span className="ml-1.5 text-[9px] opacity-70">{dr.pins?.length || 0}</span>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-500">
        <span className="text-slate-300 font-medium">{d.sheet_number}</span>
        {d.sheet_name ? ` — ${d.sheet_name}` : ''}
        {d.discipline ? ` · ${d.discipline}` : ''}
      </p>
      <FloorPlanViewer key={d.sheet_id} floorPlan={floorPlan} onMarkerClick={onPinClick} />
    </div>
  );
}


function FloorPlanViewer({ floorPlan, onMarkerClick }) {
  const [imgError, setImgError] = useState(false);
  const [hover, setHover] = useState(null);
  const markers = floorPlan.markers || [];
  const w = floorPlan.width || 1000;
  const h = floorPlan.height || 800;

  // Markers may be in pixel space (0..w / 0..h) or fractional (0..1). Normalize to %.
  const toPct = (val, dim) => {
    if (val == null) return 0;
    if (val <= 1) return val * 100;       // fractional
    return (val / dim) * 100;             // pixel space
  };

  return (
    <div className="border border-slate-800/60 rounded-sm overflow-hidden bg-slate-950" data-testid="floor-plan-viewer">
      {imgError ? (
        <div className="py-16 text-center">
          <Map className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-xs text-slate-500">Floor plan image is not available.</p>
        </div>
      ) : (
        <div className="relative w-full" style={{ aspectRatio: `${w} / ${h}` }}>
          <img src={floorPlan.image_url} alt="Floor plan" onError={() => setImgError(true)}
            className="absolute inset-0 w-full h-full object-contain" data-testid="floor-plan-image" />
          {markers.map((m, i) => (
            <div key={m.asset_id || i}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
              style={{ left: `${toPct(m.x, w)}%`, top: `${toPct(m.y, h)}%` }}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              onClick={() => onMarkerClick && m.asset_id && onMarkerClick(m.asset_id)}
              data-testid={`floor-plan-marker-${i}`}>
              <div className={`w-3 h-3 rounded-full bg-blue-500 ring-2 ring-white/70 shadow transition-transform hover:scale-150 ${onMarkerClick ? 'cursor-pointer' : ''}`} />
              {hover === i && (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-5 z-10 whitespace-nowrap px-2 py-1 rounded-sm bg-slate-900 border border-slate-700 text-[10px] text-white shadow-lg">
                  <span className="font-mono text-blue-300">{m.tag}</span>
                  {m.name && <span className="text-slate-400"> — {m.name}</span>}
                  {m.location && <span className="block text-slate-500">{m.location}</span>}
                  {onMarkerClick && <span className="block text-blue-400/80 mt-0.5">Click to view warranty &amp; O&amp;M →</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {markers.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-800/40 flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-500 ring-1 ring-white/70" />
          <span className="text-[10px] text-slate-500">Asset location — {onMarkerClick ? 'click a pin to open its warranty & O&M card' : 'hover a pin for details'}</span>
        </div>
      )}
    </div>
  );
}


function AssetCardPublic({ asset, expanded, onToggle, warrantyStart }) {
  const a = asset;
  const equipName = `${a.make || ''} ${a.model || ''} ${a.equipment_type}`.trim();
  const attrs = Object.entries(a.attributes || {}).filter(([, v]) => v);
  const hasWarranty = !!a.warranty_info?.warranty_components?.length;
  const hasOm = !!a.om_info?.tasks?.length;
  const hasDocs = !!a.om_info?.om_manual_links?.length;
  const hasUF = !!a.uniformat?.code;
  const wStart = warrantyStart ? new Date(warrantyStart) : null;
  const expiry = (years) => {
    if (!wStart || isNaN(wStart) || !years) return null;
    const d = new Date(wStart);
    d.setFullYear(d.getFullYear() + Number(years));
    return d.toISOString().slice(0, 10);
  };

  return (
    <div className="hover:bg-slate-900/30 transition-colors" data-testid={`handover-asset-${a.id}`}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={onToggle}>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
        <Cpu className="w-3.5 h-3.5 text-blue-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-white truncate">{equipName}</p>
          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500 flex-wrap">
            <span className="font-mono px-1 py-0.5 rounded bg-blue-950/30 text-blue-300 border border-blue-800/30" data-testid={`handover-asset-qty-${a.id}`}>Qty {a.quantity ?? 1}</span>
            {a.csi_number && <span className="font-mono">{a.csi_number}</span>}
            {hasUF && <span className="font-mono px-1 py-0.5 rounded bg-teal-950/30 text-teal-400 border border-teal-800/30">UF {a.uniformat.code}</span>}
            {a.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{a.location}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {hasWarranty && <Shield className="w-3 h-3 text-emerald-400" />}
          {hasOm && <Wrench className="w-3 h-3 text-amber-400" />}
          {hasDocs && <FileText className="w-3 h-3 text-blue-400" />}
          {hasUF && <Tag className="w-3 h-3 text-teal-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pl-12 space-y-3">
          {/* Attributes */}
          {attrs.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Specifications</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {attrs.map(([k, v]) => (
                  <div key={k} className="bg-slate-900/50 border border-slate-800/30 rounded-sm px-2.5 py-1.5">
                    <span className="text-[9px] text-slate-500 uppercase">{k.replace(/_/g, ' ')}</span>
                    <p className="text-[11px] text-white font-mono">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* UniFormat */}
          {hasUF && (
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">UniFormat II Classification</p>
              <div className="bg-teal-950/20 border border-teal-800/20 rounded-sm px-3 py-2">
                <p className="text-xs font-mono text-teal-400">{a.uniformat.code}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{a.uniformat.description}</p>
                {a.uniformat.levels && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {a.uniformat.levels.map((l, i) => (
                      <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-teal-900/30 border border-teal-800/20 text-teal-300/80">{l}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Warranty */}
          {hasWarranty && (
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Warranty</p>
              {a.warranty_info.warranty_summary && (
                <p className="text-[11px] text-slate-400 mb-1.5">{a.warranty_info.warranty_summary}</p>
              )}
              <div className="space-y-1">
                {a.warranty_info.warranty_components.map((wc, i) => {
                  const pExp = expiry(wc.parts_years);
                  const lExp = expiry(wc.labor_years);
                  return (
                  <div key={i} className="bg-emerald-950/20 border border-emerald-800/20 rounded-sm px-3 py-2" data-testid={`handover-asset-warranty-${a.id}-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-white flex-1 min-w-0">{wc.component || wc.name || 'Coverage'}</span>
                      {wc.parts_years ? <span className="text-[9px] font-mono text-emerald-300/90">{wc.parts_years}yr parts</span> : null}
                      {wc.labor_years ? <span className="text-[9px] font-mono text-blue-300/90">{wc.labor_years}yr labor</span> : null}
                      {wc.requires_registration && <span className="text-[9px] font-mono text-amber-400">REG</span>}
                    </div>
                    {(wc.description || wc.coverage) && <p className="text-[10px] text-slate-400 mt-0.5">{wc.description || wc.coverage}</p>}
                    {(pExp || lExp) && (
                      <div className="flex flex-wrap gap-3 mt-1" data-testid={`handover-asset-warranty-exp-${a.id}-${i}`}>
                        {pExp && <span className="text-[9px] font-mono text-emerald-300/90">Parts expires {pExp}</span>}
                        {lExp && <span className="text-[9px] font-mono text-blue-300/90">Labor expires {lExp}</span>}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              {a.warranty_info.warranty_tips?.length > 0 && (
                <div className="text-[10px] text-slate-500 space-y-0.5 mt-1.5">
                  {a.warranty_info.warranty_tips.map((t, i) => <p key={i}>• {t}</p>)}
                </div>
              )}
              <div className="flex gap-3 flex-wrap mt-1.5">
                {a.warranty_info.product_page_url && (
                  <a href={a.warranty_info.product_page_url} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1" data-testid={`handover-asset-product-${a.id}`}>
                    <ExternalLink className="w-3 h-3" /> Product Page
                  </a>
                )}
                {a.warranty_info.registration_url && (
                  <a href={a.warranty_info.registration_url} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid={`handover-asset-register-${a.id}`}>
                    <ExternalLink className="w-3 h-3" /> Register Warranty
                  </a>
                )}
                {a.warranty_info.spec_sheet_url && (
                  <a href={a.warranty_info.spec_sheet_url} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-testid={`handover-asset-spec-${a.id}`}>
                    <ExternalLink className="w-3 h-3" /> Spec Sheet
                  </a>
                )}
                {a.warranty_info.lookup_url && !a.warranty_info.registration_url && (
                  <a href={a.warranty_info.lookup_url} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid={`handover-asset-lookup-${a.id}`}>
                    <ExternalLink className="w-3 h-3" /> Warranty Lookup
                  </a>
                )}
              </div>
            </div>
          )}

          {/* OEM Documents */}
          {a.om_info?.om_manual_links?.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">OEM Documents</p>
              <div className="flex gap-2 flex-wrap" data-testid={`handover-asset-oem-docs-${a.id}`}>
                {a.om_info.om_manual_links.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-sm border bg-blue-950/30 border-blue-800/40 text-blue-300 hover:bg-blue-950/50 transition-colors"
                    data-testid={`handover-asset-oem-doc-${a.id}-${i}`}>
                    <FileText className="w-3 h-3 shrink-0" />
                    <span className="truncate max-w-[220px]">{link.title || 'Document'}</span>
                    {(link.source === 'web_search' || link.source === 'serper_google') && <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400 shrink-0" />}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* O&M */}
          {hasOm && (
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">O&M / Preventive Maintenance</p>
              {a.om_info.maintenance_notes && <p className="text-[11px] text-slate-400 mb-1.5">{a.om_info.maintenance_notes}</p>}
              <div className="space-y-1">
                {a.om_info.tasks.slice(0, 6).map((t, i) => (
                  <div key={i} className="bg-amber-950/20 border border-amber-800/20 rounded-sm px-3 py-1.5 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-white flex-1 min-w-0">{t.task}</span>
                    {t.category && <span className="text-[9px] text-slate-500 font-mono hidden md:inline">{t.category}</span>}
                    <span className="text-[9px] text-amber-400 font-mono shrink-0">{t.frequency}</span>
                  </div>
                ))}
                {a.om_info.tasks.length > 6 && (
                  <p className="text-[9px] text-slate-500">+ {a.om_info.tasks.length - 6} more tasks</p>
                )}
              </div>
              {a.om_info.critical_parts?.length > 0 && (
                <div className="mt-2">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Critical Spare Parts</p>
                  <div className="flex gap-1 flex-wrap">
                    {a.om_info.critical_parts.map((cp, i) => (
                      <span key={i} className="px-2 py-0.5 text-[10px] rounded-sm bg-slate-900/50 border border-slate-800/40 text-slate-300"
                        title={cp.description || ''}>{cp.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
